/**
 * ReceiveHistory — BLU-3xx Phase 47.01.2-B-05
 *
 * Receive History page: lists recent receipts at the currently-selected
 * location with a per-row Reverse action. Cross-org-aware (mirrors B-03's
 * owner-column + ribbon pattern when the data set spans multiple orgs).
 *
 * Default window: last 7 days (per CONTEXT.md FE-9). For 3PLs doing
 * hundreds of receipts/day an unbounded list is unscrollable; the date
 * filter exposes 7d / 30d / 90d. The active window shows as a chip on
 * the sticky bar.
 *
 * Pagination: cursor-based "Load more" — per the plan, full numbered
 * pagination is intentionally deferred. The cursor key is the last row's
 * `created_at`; the DAO does `created_at < cursor`. We accumulate pages
 * locally and reset the accumulator whenever the location, owner filter,
 * or date window changes.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { subDays } from 'date-fns';
import { CalendarRange, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PageActionBar } from '@/components/layout/PageActionBar';
import { BackToTopPill } from '@/components/BackToTopPill';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageErrorBoundary } from '@/components/fallbacks';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';

import { ClientFilterDropdown } from '@/features/inventory/components/ClientFilterDropdown';
import { ReceiveHistoryTable } from '@/features/inventory/components/ReceiveHistoryTable';
import { useHeaderLeftSlot } from '@/contexts/HeaderSlotContext';
import {
  ALL_CLIENTS,
  useClientOrgFilter,
} from '@/features/inventory/hooks/useClientOrgFilter';
import { useReceiveHistory } from '@/features/inventory/hooks/useReceiveHistory';
import type { ReceiveHistoryReceipt } from '@/features/inventory/components/ReceiveHistoryRow';

/** Date window presets shown on the sticky bar. Keys map to `subDays` arg.
 *  i18n label keys live in `inventory.json` under `receiveHistory.dateWindow.*`. */
const DATE_WINDOW_OPTIONS = [
  { value: '7', labelKey: 'receiveHistory.dateWindow.last7' },
  { value: '30', labelKey: 'receiveHistory.dateWindow.last30' },
  { value: '90', labelKey: 'receiveHistory.dateWindow.last90' },
  { value: 'all', labelKey: 'receiveHistory.dateWindow.all' },
] as const;

const PAGE_LIMIT = 50;

interface ReceiveHistoryProps {
  /**
   * When true, suppress the page-level chrome (TopHeader title, header
   * client-filter slot, sticky PageActionBar, BackToTopPill, container
   * max-width). The page renders inline controls instead so it can sit
   * inside a tab on another page (e.g. /logs?tab=receive_history).
   */
  embedded?: boolean;
}

/**
 * Wraps the page-level chrome hooks so they only run when we're the
 * standalone page. Mounting/unmounting this component is what gates
 * `usePageTitle` + `useHeaderLeftSlot` — calling those hooks conditionally
 * inside ReceiveHistory itself would break React's rules of hooks.
 */
function PageChromeBinder({
  titleText,
  subtitle,
  headerLeftSlot,
}: {
  titleText: string;
  subtitle: string | null;
  headerLeftSlot: React.ReactNode;
}) {
  usePageTitle(titleText, subtitle);
  useHeaderLeftSlot(headerLeftSlot);
  return null;
}

function ReceiveHistory({ embedded = false }: ReceiveHistoryProps) {
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  const { t } = useTranslation('inventory');

  const [windowKey, setWindowKey] =
    useState<(typeof DATE_WINDOW_OPTIONS)[number]['value']>('7');

  // Page accumulator — each "Load more" click pushes another query result
  // onto the stack. We reset whenever the upstream filter changes so the
  // cursor chain stays consistent.
  const [pages, setPages] = useState<ReceiveHistoryReceipt[][]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const fromDate = useMemo(() => {
    if (windowKey === 'all') return undefined;
    return subDays(new Date(), Number(windowKey)).toISOString();
  }, [windowKey]);

  // The list of distinct orgs in the data set drives the per-client filter
  // dropdown's option list. We hand it the *accumulated* pages so the
  // dropdown stays stable across "Load more" clicks. The hook also fetches
  // resolved client names from `listConnectedClientsAtLocation`.
  const accumulatedRows = useMemo(() => pages.flat(), [pages]);

  const filter = useClientOrgFilter<ReceiveHistoryReceipt>({
    rows: accumulatedRows,
    ownOrgId: organization?.id,
    ownOrgName: organization?.name,
    locationId: selectedLocation?.id,
  });

  const ownerOrgIdServerFilter =
    filter.clientOrgFilter === ALL_CLIENTS ? undefined : filter.clientOrgFilter;

  // Reset the cursor + accumulated pages + the appended-cursor sentinel
  // whenever the upstream filter changes. Critical for correctness: a stale
  // cursor from a different ownerOrgId / fromDate window would skip rows on
  // the next Load More.
  const lastAppendedCursorRef = useRef<string | null | undefined>(null);
  useEffect(() => {
    setCursor(undefined);
    setPages([]);
    lastAppendedCursorRef.current = null;
  }, [selectedLocation?.id, ownerOrgIdServerFilter, fromDate]);

  const query = useReceiveHistory({
    locationId: selectedLocation?.id ?? '',
    ownerOrgId: ownerOrgIdServerFilter,
    fromDate,
    cursor,
    limit: PAGE_LIMIT,
    enabled: !!selectedLocation?.id,
  });

  // Append each successful page to the accumulator. We key off the cursor we
  // sent so a re-render with the same data doesn't double-append. The
  // sentinel is reset (above) when the upstream filter changes.
  useEffect(() => {
    if (!query.data) return;
    if (lastAppendedCursorRef.current === cursor) return;
    lastAppendedCursorRef.current = cursor;
    const fetchedReceipts = query.data.receipts as ReceiveHistoryReceipt[];
    setPages((prev) =>
      cursor === undefined ? [fetchedReceipts] : [...prev, fetchedReceipts],
    );
  }, [query.data, cursor]);

  const handleLoadMore = () => {
    const nextCursor = query.data?.nextCursor;
    if (nextCursor) setCursor(nextCursor);
  };

  // The owner column + ribbon ONLY render when:
  //  1) the operator has cross-org access at this location (capability gate
  //     — same gate used by the header client filter, BLU-3xx B-fix3.5
  //     BUG 5d). Pre-fix3.6 we gated on `distinctOrgsInData.length > 1`
  //     which hid the owner column whenever a client's full data set fit
  //     on one page — even though the operator was clearly cross-org.
  //  2) the per-client filter is "All clients" (when narrowed, the page-
  //     level banner already names the org and per-row badges would be
  //     redundant per B-03 SUMMARY).
  const showOwnerColumn =
    filter.clientOrgFilter === ALL_CLIENTS && filter.showClientFilter;

  // Drive the renderable receipts off the accumulator (so Load More extends
  // the list) AND apply the client filter when active. Server-side filtering
  // gives us correct page sizes; the client filter call is a defensive
  // pass-through for old pages received before the filter changed.
  const visibleReceipts = filter.filteredRows;

  // Loading on first fetch only — for "Load more" we keep showing the
  // accumulator and put the spinner on the button.
  const isInitialLoading = query.isLoading && pages.length === 0;

  // Empty-but-filtered: distinct copy from "no rows yet at this location."
  const isFilteredEmpty =
    !isInitialLoading &&
    visibleReceipts.length === 0 &&
    filter.clientOrgFilter !== ALL_CLIENTS;

  // BLU-3xx Phase 47.01.2-B-fix3.4: cross-org client filter lives in the
  // global TopHeader slot when this is the standalone page. When embedded
  // inside Logs, the same memoized JSX is rendered inline alongside the
  // date window so the filter sits *with* the receipts data and doesn't
  // bleed across the other Logs tabs.
  const ownOrgId = organization?.id;
  const clientFilterDropdown = useMemo(
    () =>
      filter.showClientFilter ? (
        <ClientFilterDropdown
          value={filter.clientOrgFilter}
          onChange={filter.setClientOrgFilter}
          ownOrgId={ownOrgId}
          orgIdToName={filter.orgIdToName}
          distinctOrgsInData={filter.distinctOrgsInData}
        />
      ) : null,
    [
      filter.showClientFilter,
      filter.clientOrgFilter,
      filter.setClientOrgFilter,
      filter.orgIdToName,
      filter.distinctOrgsInData,
      ownOrgId,
    ],
  );

  const dateWindowSelect = (
    <>
      <CalendarRange
        className="h-4 w-4 text-muted-foreground self-center shrink-0"
        aria-hidden="true"
      />
      <Select
        value={windowKey}
        onValueChange={(value) =>
          setWindowKey(
            value as (typeof DATE_WINDOW_OPTIONS)[number]['value'],
          )
        }
      >
        <SelectTrigger
          className="h-8 w-[160px] text-xs"
          aria-label={t('receiveHistory.dateWindowAriaLabel')}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DATE_WINDOW_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );

  return (
    <div className="flex flex-col">
      {!embedded && (
        <PageChromeBinder
          titleText={t('receiveHistory.title')}
          subtitle={selectedLocation?.name ?? null}
          headerLeftSlot={clientFilterDropdown}
        />
      )}

      <div className={embedded ? '' : 'container py-6 max-w-full px-6'}>
        {embedded ? (
          <div className="flex flex-wrap items-center gap-2">
            {dateWindowSelect}
            {clientFilterDropdown}
          </div>
        ) : (
          <PageActionBar
            ariaLabel={t('receiveHistory.actionsAriaLabel')}
            left={dateWindowSelect}
            right={null}
          />
        )}

        <div className="mt-4 space-y-4">
          {!selectedLocation ? (
            <div className="rounded-md border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
              {t('receiveHistory.chooseLocationHint')}
            </div>
          ) : query.error ? (
            <div className="rounded-md border bg-destructive/5 p-6 text-sm text-destructive">
              <p className="font-medium">{t('receiveHistory.errorTitle')}</p>
              <p className="mt-1 text-xs">{query.error.message}</p>
              <Button
                size="sm"
                variant="outline"
                className="mt-3 h-8 px-3 text-xs"
                onClick={() => query.refetch()}
              >
                {t('receiveHistory.retry')}
              </Button>
            </div>
          ) : isFilteredEmpty ? (
            <div className="rounded-md border bg-muted/20 p-8 text-center text-sm">
              <p className="font-medium text-foreground">
                {t('receiveHistory.emptyFiltered', {
                  client: filter.selectedClientName ?? 'client',
                })}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('receiveHistory.empty')}
              </p>
              <Button
                size="sm"
                variant="outline"
                className="mt-3 h-8 px-3 text-xs"
                onClick={() => filter.setClientOrgFilter(ALL_CLIENTS)}
              >
                {t('crossOrg.banner.viewAll')}
              </Button>
            </div>
          ) : (
            <>
              <ReceiveHistoryTable
                receipts={visibleReceipts}
                isLoading={isInitialLoading}
                showOwnerColumn={showOwnerColumn}
                ownOrgId={organization?.id}
                orgIdToName={filter.orgIdToName}
                onReverseSuccess={(receiptId) => {
                  // BLU-3xx Phase 47.01.2-B-fix3.5 (BUG 4): optimistically
                  // remove the reversed row from each accumulated page so
                  // the UI updates instantly. The backend invalidation
                  // (fired by the table) refetches and confirms.
                  setPages((prev) =>
                    prev.map((page) =>
                      page.filter((r) => r.id !== receiptId),
                    ),
                  );
                }}
              />

              {query.data?.nextCursor && (
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-xs gap-1.5"
                    onClick={handleLoadMore}
                    disabled={query.isFetching}
                  >
                    {query.isFetching ? (
                      <>
                        <Loader2
                          className="h-3.5 w-3.5 animate-spin"
                          aria-hidden="true"
                        />
                        {t('receiveHistory.loadingMore')}
                      </>
                    ) : (
                      t('receiveHistory.loadMore')
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {!embedded && <BackToTopPill />}
    </div>
  );
}

export default function ReceiveHistoryWithErrorBoundary(props: ReceiveHistoryProps) {
  return (
    <PageErrorBoundary>
      <ReceiveHistory {...props} />
    </PageErrorBoundary>
  );
}
